import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';
import { Button } from '../../components/ui/Button';
import Sidebar from '../../components/ui/Sidebar';
import DateRangeFilter from './components/DateRangeFilter';
import { startOfDay, endOfDay, subDays, format } from 'date-fns';
import * as partnerService from '../../services/partnerService';
import { supabase } from '../../services/supabaseClient';
import { 
  Eye, 
  Users, 
  DollarSign, 
  TrendingUp, 
  RefreshCw, 
  Plus, 
  PieChart, 
  Edit, 
  ToggleRight, 
  ToggleLeft, 
  Trash2,
  AlertCircle,
  TrendingDown,
  BarChart3,
  Target,
  UserCheck
} from 'lucide-react';

interface OfferPerformance {
  offer_id: string;
  offer_title: string;
  offer_status: string;
  redemption_count: number;
  total_savings: number;
  last_redemption_date?: string;
}

// NEW: Enhanced offer performance with engagement and ROI metrics
interface EnhancedOfferMetrics {
  offer_id: string;
  offer_title: string;
  redemption_count: number;
  unique_customers: number;
  customer_engagement_rate: number;
  avg_redemption_value: number;
  total_revenue: number;
  roi_score: number;
  redemption_trend: 'up' | 'down' | 'stable';
  repeat_customer_rate: number;
}

const PartnerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { partner, loading: authLoading, signOut } = usePartnerAuth();
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState({
    total_offers: 0,
    active_offers: 0,
    total_redemptions: 0,
    total_savings: 0,
    average_redemption_value: 0,
    active_offer_rate: 0,
    repeat_customer_rate: 0,
    average_redemption_amount_per_user: 0,
    total_redemption_amount: 0
  });
  const [offerPerformance, setOfferPerformance] = useState<OfferPerformance[]>([]);
  
  // NEW: Enhanced metrics state for individual offer performance
  const [enhancedMetrics, setEnhancedMetrics] = useState<EnhancedOfferMetrics[]>([]);
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const [offerCapacity, setOfferCapacity] = useState({
    currentCount: 0,
    maxAllowed: 5,
    remaining: 5,
    canCreate: true
  });

  const [statsView, setStatsView] = useState<'cumulative' | 'by-offer'>('cumulative');
  
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: startOfDay(subDays(new Date(), 30)),
    endDate: endOfDay(new Date())
  });

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/partner-login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    if (!authLoading && partner) {
      loadPartnerData();
      loadOfferCapacity();
    }
  }, [partner, authLoading, navigate]);

  const loadOfferCapacity = async () => {
    if (!partner) return;
    
    try {
      const { data, error } = await supabase.rpc('can_partner_create_offer', {
        p_partner_id: partner.id
      });

      if (error) throw error;

      if (data && data.length > 0) {
        const capacity = data[0];
        setOfferCapacity({
          currentCount: capacity.current_count,
          maxAllowed: capacity.max_allowed,
          remaining: capacity.remaining,
          canCreate: capacity.can_create
        });
      }
    } catch (error) {
      console.error('Error loading offer capacity:', error);
    }
  };

  const loadPartnerData = async () => {
    if (!partner) return;
    
    try {
      setLoading(true);

      const stats = await partnerService.getPartnerStatistics(
        partner.id,
        dateRange.startDate || undefined,
        dateRange.endDate || undefined
      );
      
      setStatistics({
        total_offers: stats.offersCount,
        active_offers: stats.activeOffersCount,
        total_redemptions: stats.totalRedemptions,
        total_savings: stats.totalRevenue,
        average_redemption_value: stats.averageRedemptionValue,
        active_offer_rate: stats.activeOfferRate,
        repeat_customer_rate: stats.repeatCustomerRate,
        average_redemption_amount_per_user: stats.averageRedemptionAmountPerUser,
        total_redemption_amount: stats.totalRedemptionAmount
      });

      const performance = await partnerService.getPartnerOfferPerformance(
        partner.id,
        dateRange.startDate || undefined,
        dateRange.endDate || undefined
      );
      
      const mappedPerformance = performance.offerPerformance.map((offer: any) => ({
        offer_id: offer.offerId,
        offer_title: offer.offerTitle,
        offer_status: offer.offerStatus,
        redemption_count: offer.redemptionCount,
        total_savings: parseFloat(offer.savings),
        last_redemption_date: offer.lastRedemptionDate
      }));
      
      setOfferPerformance(mappedPerformance);

      // NEW: Load enhanced metrics for individual offer performance
      await loadEnhancedOfferMetrics(partner.id);

    } catch (error) {
      console.error('Error loading partner data:', error);
      alert('Failed to load partner data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // NEW: Load enhanced metrics with customer engagement and ROI data
  const loadEnhancedOfferMetrics = async (partnerId: string) => {
    try {
      // Fetch detailed redemption data per offer
      const { data: redemptionData, error } = await supabase
        .from('redemptions')
        .select(`
          offer_id,
          user_id,
          final_price,
          saved_amount,
          redemption_date,
          offers!inner(
            title,
            partner_id,
            regular_price
          )
        `)
        .eq('offers.partner_id', partnerId)
        .gte('redemption_date', dateRange.startDate?.toISOString())
        .lte('redemption_date', dateRange.endDate?.toISOString())
        .order('redemption_date', { ascending: true });

      if (error) throw error;

      // Process data to calculate enhanced metrics
      const offerMetricsMap = new Map<string, {
        offer_id: string;
        offer_title: string;
        redemptions: Array<{ user_id: string; final_price: number; saved_amount: number; date: string }>;
        unique_users: Set<string>;
        repeat_users: Set<string>;
        total_revenue: number;
        total_savings: number;
      }>();

      redemptionData?.forEach((redemption: any) => {
        const offerId = redemption.offer_id;
        
        if (!offerMetricsMap.has(offerId)) {
          offerMetricsMap.set(offerId, {
            offer_id: offerId,
            offer_title: redemption.offers.title,
            redemptions: [],
            unique_users: new Set(),
            repeat_users: new Set(),
            total_revenue: 0,
            total_savings: 0
          });
        }

        const metrics = offerMetricsMap.get(offerId)!;
        metrics.redemptions.push({
          user_id: redemption.user_id,
          final_price: redemption.final_price,
          saved_amount: redemption.saved_amount,
          date: redemption.redemption_date
        });

        // Track unique and repeat customers
        if (metrics.unique_users.has(redemption.user_id)) {
          metrics.repeat_users.add(redemption.user_id);
        }
        metrics.unique_users.add(redemption.user_id);
        
        metrics.total_revenue += parseFloat(redemption.final_price || 0);
        metrics.total_savings += parseFloat(redemption.saved_amount || 0);
      });

      // Calculate enhanced metrics for each offer
      const enhanced: EnhancedOfferMetrics[] = Array.from(offerMetricsMap.values()).map(metrics => {
        const redemption_count = metrics.redemptions.length;
        const unique_customers = metrics.unique_users.size;
        const repeat_customers = metrics.repeat_users.size;
        
        // Calculate customer engagement rate (repeat customers / unique customers)
        const customer_engagement_rate = unique_customers > 0 
          ? (repeat_customers / unique_customers) * 100 
          : 0;

        // Calculate average redemption value
        const avg_redemption_value = redemption_count > 0 
          ? metrics.total_revenue / redemption_count 
          : 0;

        // Calculate ROI score (higher redemptions + engagement + revenue = better ROI)
        const roi_score = (
          (redemption_count * 0.4) + 
          (customer_engagement_rate * 0.3) + 
          (unique_customers * 0.3)
        );

        // Calculate redemption trend (comparing first half vs second half of date range)
        const midpoint = Math.floor(metrics.redemptions.length / 2);
        const firstHalfCount = midpoint;
        const secondHalfCount = metrics.redemptions.length - midpoint;
        
        let redemption_trend: 'up' | 'down' | 'stable' = 'stable';
        if (secondHalfCount > firstHalfCount * 1.1) {
          redemption_trend = 'up';
        } else if (secondHalfCount < firstHalfCount * 0.9) {
          redemption_trend = 'down';
        }

        // Calculate repeat customer rate for this offer
        const repeat_customer_rate = unique_customers > 0 
          ? (repeat_customers / unique_customers) * 100 
          : 0;

        return {
          offer_id: metrics.offer_id,
          offer_title: metrics.offer_title,
          redemption_count,
          unique_customers,
          customer_engagement_rate: Math.round(customer_engagement_rate),
          avg_redemption_value,
          total_revenue: metrics.total_revenue,
          roi_score: Math.round(roi_score),
          redemption_trend,
          repeat_customer_rate: Math.round(repeat_customer_rate)
        };
      });

      setEnhancedMetrics(enhanced);
    } catch (error) {
      console.error('Error loading enhanced metrics:', error);
    }
  };

  // Reload data when date range changes - ONLY if partner exists
  useEffect(() => {
    if (partner && dateRange.startDate && dateRange.endDate) {
      loadPartnerData();
    }
  }, [dateRange]);

  const handleDateRangeChange = (startDate: Date | null, endDate: Date | null) => {
    setDateRange({ startDate, endDate });
  };

  const handleCreateOffer = () => {
    if (!offerCapacity.canCreate) {
      alert(`You have reached your maximum offer limit (${offerCapacity.currentCount}/${offerCapacity.maxAllowed} offers used). Please contact admin to increase your limit.`);
      return;
    }
    navigate('/partner-create-offer');
  };

  const handleDeleteOffer = async (offerId: string, offerTitle: string) => {
    if (!window.confirm(`Are you sure you want to delete "${offerTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await partnerService.deleteOffer(offerId);
      alert('Offer deleted successfully');
      loadPartnerData();
    } catch (error) {
      console.error('Error deleting offer:', error);
      alert('Failed to delete offer. Please try again.');
    }
  };

  const handleToggleOfferStatus = async (offerId: string, currentStatus: string, offerTitle: string) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const action = newStatus === 'active' ? 'activate' : 'deactivate';
    
    if (!window.confirm(`Are you sure you want to ${action} "${offerTitle}"?`)) {
      return;
    }

    try {
      if (newStatus === 'active') {
        await partnerService.activateOffer(offerId);
      } else {
        await partnerService.deactivateOffer(offerId);
      }
      
      alert(`Offer ${action}d successfully`);
      loadPartnerData();
    } catch (error) {
      console.error(`Error ${action}ing offer:`, error);
      alert(`Failed to ${action} offer. Please try again.`);
    }
  };

  const handleEditOffer = (offerId: string) => {
    navigate(`/partner-create-offer?edit=${offerId}`);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // NEW: Get enhanced metrics for selected offer
  const getOfferEnhancedMetrics = (offerId: string) => {
    return enhancedMetrics.find(m => m.offer_id === offerId);
  };

  // Show loading spinner during auth check or data loading
  if (authLoading || loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {authLoading ? 'Checking authentication...' : 'Loading partner dashboard...'}
          </p>
        </div>
      </div>
    );
  }

  // NEW: Calculate stats based on selected view
  const displayStats = statsView === 'cumulative' ? statistics : {
    // When viewing by offer, show 0 for cumulative stats
    total_offers: statistics.total_offers,
    active_offers: statistics.active_offers,
    total_redemptions: 0,
    total_savings: 0,
    average_redemption_value: 0,
    active_offer_rate: 0,
    repeat_customer_rate: 0,
    average_redemption_amount_per_user: 0,
    total_redemption_amount: 0
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar - Fixed on the left - CRITICAL FIX: Pass userRole='partner' */}
      <Sidebar 
        isCollapsed={sidebarCollapsed}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        userRole="partner"
      />
      
      {/* Main content area with header */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header - Next to sidebar horizontally */}
        <header className="bg-white shadow-sm border-b border-gray-200 h-16 flex items-center justify-between px-6 shrink-0">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Partner Dashboard</h1>
            <p className="text-xs text-gray-600">
              {partner?.businessName} ({partner?.partnerCode})
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Logout
          </button>
        </header>

        {/* Scrollable main content */}
        <main className="flex-1 overflow-y-auto p-6">
          {/* Welcome Header */}
          <div className="mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Welcome, {partner?.businessName}
            </h2>
            <p className="text-sm sm:text-base text-gray-600">Monitor your offer performance and customer engagement</p>
          </div>

          {/* Date Range Filter */}
          <DateRangeFilter onDateRangeChange={handleDateRangeChange} />

          {/* NEW: Stats View Selector & Offer Capacity Card */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-gray-700">View Stats:</label>
                <select
                  value={statsView}
                  onChange={(e) => setStatsView(e.target.value as 'cumulative' | 'by-offer')}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="cumulative">All Offers (Cumulative)</option>
                  <option value="by-offer">By Individual Offer</option>
                </select>
              </div>
              
              {/* NEW: Offer Capacity Display */}
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                offerCapacity.remaining === 0 ? 'bg-red-50 border border-red-200' : 
                offerCapacity.remaining <= 2 ? 'bg-yellow-50 border border-yellow-200': 'bg-blue-50 border border-blue-200'
              }`}>
                <AlertCircle className={`w-5 h-5 ${
                  offerCapacity.remaining === 0 ? 'text-red-600' : 
                  offerCapacity.remaining <= 2 ? 'text-yellow-600': 'text-blue-600'
                }`} />
                <span className="text-sm font-semibold">
                  Offers: {offerCapacity.currentCount}/{offerCapacity.maxAllowed}
                </span>
                <span className="text-xs text-gray-600">
                  ({offerCapacity.remaining} remaining)
                </span>
              </div>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-4 mb-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm opacity-90">Active Offers Shown</p>
                <Eye className="w-5 h-5 opacity-80" />
              </div>
              <p className="text-3xl font-bold">{displayStats.total_offers}</p>
              <p className="text-xs opacity-80 mt-1">
                Currently visible to customers
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm opacity-90">Total Redemptions</p>
                <Users className="w-5 h-5 opacity-80" />
              </div>
              <p className="text-3xl font-bold">{displayStats.total_redemptions}</p>
              <p className="text-xs opacity-80 mt-1">Customer engagements</p>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm opacity-90">Total Savings</p>
                <DollarSign className="w-5 h-5 opacity-80" />
              </div>
              <p className="text-3xl font-bold">
                {formatCurrency(displayStats.total_savings)}
              </p>
              <p className="text-xs opacity-80 mt-1">Generated for customers</p>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm opacity-90">Offer Success Rate</p>
                <TrendingUp className="w-5 h-5 opacity-80" />
              </div>
              <p className="text-3xl font-bold">
                {displayStats.active_offer_rate}%
              </p>
              <p className="text-xs opacity-80 mt-1">Active offers with redemptions</p>
            </div>

            <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm opacity-90">Repeat Customers</p>
                <RefreshCw className="w-5 h-5 opacity-80" />
              </div>
              <p className="text-3xl font-bold">
                {displayStats.repeat_customer_rate}%
              </p>
              <p className="text-xs opacity-80 mt-1">Users redeeming 2+ offers</p>
            </div>

            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm opacity-90">Avg Amount Per User</p>
                <DollarSign className="w-5 h-5 opacity-80" />
              </div>
              <p className="text-3xl font-bold">
                {formatCurrency(displayStats.average_redemption_amount_per_user)}
              </p>
              <p className="text-xs opacity-80 mt-1">Average redemption per user</p>
            </div>

            <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm opacity-90">Total Amount</p>
                <DollarSign className="w-5 h-5 opacity-80" />
              </div>
              <p className="text-3xl font-bold">
                {formatCurrency(displayStats.total_redemption_amount)}
              </p>
              <p className="text-xs opacity-80 mt-1">Total across all offers</p>
            </div>
          </div>

          {/* Actions Bar */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Your Offers</h2>
              <Button
                onClick={handleCreateOffer}
                className="flex items-center gap-2"
                disabled={!offerCapacity.canCreate}
              >
                <Plus className="w-4 h-4" />
                Create New Offer
                {!offerCapacity.canCreate && (
                  <span className="ml-2 text-xs">(Limit Reached)</span>
                )}
              </Button>
            </div>
          </div>

          {/* NEW: Enhanced Offer Performance Table with Individual Metrics */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Offer Performance</h3>
              <p className="text-sm text-gray-600">
                Track individual offer metrics, redemption trends, customer engagement, and ROI
                {statsView === 'by-offer' && ' (click any offer to see detailed analytics)'}
              </p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Offer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Redemptions
                    </th>
                    {statsView === 'by-offer' && (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Unique Customers
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Engagement Rate
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ROI Score
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Trend
                        </th>
                      </>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Savings
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Redeemed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {offerPerformance.length === 0 ? (
                    <tr>
                      <td colSpan={statsView === 'by-offer' ? 10 : 6} className="px-6 py-8 text-center text-gray-500">
                        <div className="flex flex-col items-center">
                          <PieChart className="w-12 h-12 text-gray-300 mb-3" />
                          <p className="text-lg font-medium mb-2">No offers yet</p>
                          <p className="text-sm mb-4">Create your first offer to start attracting customers</p>
                          <Button 
                            onClick={handleCreateOffer} 
                            className="flex items-center gap-2"
                            disabled={!offerCapacity.canCreate}
                          >
                            <Plus className="w-4 h-4" />
                            Create Offer
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    offerPerformance.map((offer) => {
                      const enhancedMetric = getOfferEnhancedMetrics(offer.offer_id);
                      
                      return (
                        <tr 
                          key={offer.offer_id} 
                          className={`hover:bg-gray-50 ${selectedOfferId === offer.offer_id ? 'bg-blue-50' : ''}`}
                          onClick={() => statsView === 'by-offer' && setSelectedOfferId(offer.offer_id)}
                        >
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">
                              {offer.offer_title}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                              ${offer.offer_status === 'active' ? 'bg-green-100 text-green-800' : 
                                offer.offer_status === 'inactive' ? 'bg-gray-100 text-gray-800' : 'bg-red-100 text-red-800'}
                            `}>
                              {offer.offer_status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Users className="w-4 h-4 text-gray-400 mr-2" />
                              <span className="text-sm font-medium text-gray-900">
                                {offer.redemption_count}
                              </span>
                            </div>
                          </td>
                          {statsView === 'by-offer' && enhancedMetric && (
                            <>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <UserCheck className="w-4 h-4 text-gray-400 mr-2" />
                                  <span className="text-sm font-medium text-gray-900">
                                    {enhancedMetric.unique_customers}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <Target className="w-4 h-4 text-gray-400 mr-2" />
                                  <span className="text-sm font-medium text-gray-900">
                                    {enhancedMetric.customer_engagement_rate}%
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <BarChart3 className="w-4 h-4 text-gray-400 mr-2" />
                                  <span className={`text-sm font-bold ${
                                    enhancedMetric.roi_score >= 70 ? 'text-green-600' :
                                    enhancedMetric.roi_score >= 40 ? 'text-yellow-600' : 'text-red-600'
                                  }`}>
                                    {enhancedMetric.roi_score}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  {enhancedMetric.redemption_trend === 'up' && (
                                    <>
                                      <TrendingUp className="w-5 h-5 text-green-600 mr-1" />
                                      <span className="text-sm font-medium text-green-600">Growing</span>
                                    </>
                                  )}
                                  {enhancedMetric.redemption_trend === 'down' && (
                                    <>
                                      <TrendingDown className="w-5 h-5 text-red-600 mr-1" />
                                      <span className="text-sm font-medium text-red-600">Declining</span>
                                    </>
                                  )}
                                  {enhancedMetric.redemption_trend === 'stable' && (
                                    <>
                                      <RefreshCw className="w-5 h-5 text-gray-600 mr-1" />
                                      <span className="text-sm font-medium text-gray-600">Stable</span>
                                    </>
                                  )}
                                </div>
                              </td>
                            </>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <DollarSign className="w-4 h-4 text-gray-400 mr-2" />
                              <span className="text-sm font-medium text-gray-900">
                                {formatCurrency(offer.total_savings)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(offer.last_redemption_date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditOffer(offer.offer_id);
                                }}
                                className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                                title="Edit Offer"
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleOfferStatus(
                                    offer.offer_id, 
                                    offer.offer_status, 
                                    offer.offer_title
                                  );
                                }}
                                className={`p-1 rounded hover:bg-opacity-10 ${
                                  offer.offer_status === 'active' ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'
                                }`}
                                title={offer.offer_status === 'active' ? 'Deactivate' : 'Activate'}
                              >
                                {offer.offer_status === 'active' ? (
                                  <ToggleRight className="w-5 h-5" />
                                ) : (
                                  <ToggleLeft className="w-5 h-5" />
                                )}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteOffer(offer.offer_id, offer.offer_title);
                                }}
                                className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                                title="Delete Offer"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* NEW: Selected Offer Detailed Analytics Card */}
          {statsView === 'by-offer' && selectedOfferId && (
            (() => {
              const selectedMetric = getOfferEnhancedMetrics(selectedOfferId);
              const selectedOffer = offerPerformance.find(o => o.offer_id === selectedOfferId);
              
              if (!selectedMetric || !selectedOffer) return null;

              return (
                <div className="mt-6 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {selectedOffer.offer_title} - Detailed Analytics
                      </h3>
                      <p className="text-sm text-gray-600">
                        Performance breakdown for the selected date range
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedOfferId(null)}
                      className="text-gray-500 hover:text-gray-700 p-1"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium text-gray-500 uppercase">Total Redemptions</p>
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <p className="text-2xl font-bold text-gray-900">{selectedMetric.redemption_count}</p>
                      <p className="text-xs text-gray-500 mt-1">Customer engagements</p>
                    </div>

                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium text-gray-500 uppercase">Unique Customers</p>
                        <UserCheck className="w-5 h-5 text-green-600" />
                      </div>
                      <p className="text-2xl font-bold text-gray-900">{selectedMetric.unique_customers}</p>
                      <p className="text-xs text-gray-500 mt-1">Individual users</p>
                    </div>

                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium text-gray-500 uppercase">Engagement Rate</p>
                        <Target className="w-5 h-5 text-purple-600" />
                      </div>
                      <p className="text-2xl font-bold text-gray-900">{selectedMetric.customer_engagement_rate}%</p>
                      <p className="text-xs text-gray-500 mt-1">Repeat customers</p>
                    </div>

                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium text-gray-500 uppercase">ROI Score</p>
                        <BarChart3 className="w-5 h-5 text-orange-600" />
                      </div>
                      <p className={`text-2xl font-bold ${
                        selectedMetric.roi_score >= 70 ? 'text-green-600' :
                        selectedMetric.roi_score >= 40 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {selectedMetric.roi_score}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Performance index</p>
                    </div>

                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium text-gray-500 uppercase">Avg Transaction</p>
                        <DollarSign className="w-5 h-5 text-teal-600" />
                      </div>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(selectedMetric.avg_redemption_value)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Per redemption</p>
                    </div>

                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium text-gray-500 uppercase">Total Revenue</p>
                        <DollarSign className="w-5 h-5 text-indigo-600" />
                      </div>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(selectedMetric.total_revenue)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Generated</p>
                    </div>

                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium text-gray-500 uppercase">Redemption Trend</p>
                        {selectedMetric.redemption_trend === 'up' ? (
                          <TrendingUp className="w-5 h-5 text-green-600" />
                        ) : selectedMetric.redemption_trend === 'down' ? (
                          <TrendingDown className="w-5 h-5 text-red-600" />
                        ) : (
                          <RefreshCw className="w-5 h-5 text-gray-600" />
                        )}
                      </div>
                      <p className={`text-2xl font-bold ${
                        selectedMetric.redemption_trend === 'up' ? 'text-green-600' :
                        selectedMetric.redemption_trend === 'down' ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {selectedMetric.redemption_trend === 'up' ? 'Growing' :
                         selectedMetric.redemption_trend === 'down' ? 'Declining' : 'Stable'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Current trajectory</p>
                    </div>

                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium text-gray-500 uppercase">Repeat Rate</p>
                        <RefreshCw className="w-5 h-5 text-pink-600" />
                      </div>
                      <p className="text-2xl font-bold text-gray-900">{selectedMetric.repeat_customer_rate}%</p>
                      <p className="text-xs text-gray-500 mt-1">Returning customers</p>
                    </div>
                  </div>

                  <div className="mt-6 bg-white rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Performance Insights</h4>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        <span>
                          This offer has attracted <strong>{selectedMetric.unique_customers}</strong> unique customers
                          {selectedMetric.customer_engagement_rate > 0 && (
                            <>, with <strong>{selectedMetric.customer_engagement_rate}%</strong> becoming repeat customers</>
                          )}
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        <span>
                          Average transaction value: <strong>{formatCurrency(selectedMetric.avg_redemption_value)}</strong>
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        <span>
                          ROI Score of <strong>{selectedMetric.roi_score}</strong> indicates {
                            selectedMetric.roi_score >= 70 ? 'excellent' :
                            selectedMetric.roi_score >= 40 ? 'good' : 'below average'
                          } performance
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        <span>
                          Redemption trend is <strong>{selectedMetric.redemption_trend}</strong>
                          {selectedMetric.redemption_trend === 'up' && ' - great momentum!'}
                          {selectedMetric.redemption_trend === 'down' && ' - consider adjusting the offer'}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              );
            })()
          )}

          {/* Performance Insights */}
          {offerPerformance.length > 0 && statsView === 'cumulative' && (
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start">
                <TrendingUp className="w-6 h-6 text-blue-600 mr-3 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">Performance Insights</h3>
                  <ul className="space-y-2 text-sm text-blue-800">
                    <li>
                      • Your most popular offer has <strong>{Math.max(...offerPerformance.map(o => o.redemption_count))}</strong> redemptions
                    </li>
                    <li>
                      • Total customer savings generated: <strong>{formatCurrency(statistics.total_savings)}</strong>
                    </li>
                    <li>
                      • Average transaction value: <strong>{formatCurrency(statistics.average_redemption_value)}</strong>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default PartnerDashboard;